import React, { useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useEmojiStyles from '../../styles/kozelDarkQuiz/emoji';

// Import imagini
import stencilBeer from '../../assets/images/kozel_dark_quiz/stencil-beer.png';
import stencilCute from '../../assets/images/kozel_dark_quiz/stencil-cute (1).png';
import stencilGlasses from '../../assets/images/kozel_dark_quiz/stencil-glasses (2).png';
import stencilJoy from '../../assets/images/kozel_dark_quiz/stencil-joy.png';
import stencilKiss from '../../assets/images/kozel_dark_quiz/stencil-kiss (1).png';

interface Template {
  id: number;
  src: string;
  name: string;
}

const templates: Template[] = [
  { id: 1, src: stencilBeer, name: 'Beer Stencil' },
  { id: 2, src: stencilCute, name: 'Cute Stencil' },
  { id: 3, src: stencilGlasses, name: 'Glasses Stencil' },
  { id: 4, src: stencilJoy, name: 'Joy Stencil' },
  { id: 5, src: stencilKiss, name: 'Kiss Stencil' },
];

const Emoji: React.FC = () => {
  const classes = useEmojiStyles();
  const [currentIndex, setCurrentIndex] = useState<number>(2); // Start cu mijlocul (index 2)
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === 0 ? templates.length - 1 : prev - 1));
      setIsTransitioning(false);
    }, 300);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentIndex((prev) => (prev === templates.length - 1 ? 0 : prev + 1));
      setIsTransitioning(false);
    }, 300);
  };

  const handleDownload = async () => {
    const selectedStencil = templates[currentIndex];
    if (!selectedStencil) return;

    try {
      const response = await fetch(selectedStencil.src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `kozel-stencil-${selectedStencil.name.toLowerCase().replace(' ', '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  // Funcție pentru a rearanja template-urile cu cel curent în mijloc
  const getOrderedTemplates = () => {
    const ordered = [];
    const totalTemplates = templates.length;
    
    // Calculez indexurile pentru pozițiile: [currentIndex-2, currentIndex-1, currentIndex, currentIndex+1, currentIndex+2]
    for (let i = -2; i <= 2; i++) {
      let index = (currentIndex + i + totalTemplates) % totalTemplates;
      ordered.push({
        template: templates[index],
        position: i, // -2, -1, 0, 1, 2 (0 = mijloc)
        originalIndex: index
      });
    }
    
    return ordered;
  };

  // Pentru desktop - afișează toate 5 template-urile rearanjate
  const getDesktopTemplates = () => {
    const orderedTemplates = getOrderedTemplates();
    
    return orderedTemplates.map((item, index) => (
      <Box
        key={`${item.template.id}-${item.position}`}
        className={`${classes.templateCircle} ${
          item.position === 0 ? classes.selectedTemplate : classes.inactiveTemplate
        }`}
        style={{
          opacity: isTransitioning ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <img 
          src={item.template.src} 
          alt={item.template.name}
          className={classes.stencilImage}
        />
      </Box>
    ));
  };

  // Pentru mobile - afișează doar template-ul curent
  const getMobileTemplate = () => {
    const template = templates[currentIndex];
    return (
      <Box 
        className={`${classes.templateCircle} ${classes.selectedTemplate}`}
        style={{
          opacity: isTransitioning ? 0.6 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <img 
          src={template.src} 
          alt={template.name}
          className={classes.stencilImage}
        />
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h3" className={classes.title}>
        DESCARCĂ ȘABLONUL PREFERAT
      </Typography>
      
      <Typography variant="body1" className={classes.description}>
        Kozel Cinnamon Art nu este doar distractivă de băut, ci și de servit.
        <br />
        De aceea, am pregătit un set de șabloane jucăușe, pe care le poți imprima acasă
        <br />
        și le poți folosi pentru a te exprima într-un mod nou și unic ;-)
      </Typography>

      <Box className={classes.templatesContainer}>
        <IconButton 
          className={classes.arrowButton}
          onClick={handlePrevious}
          disabled={isTransitioning}
        >
          <ArrowBackIosIcon />
        </IconButton>

        {/* Desktop View - toate template-urile rearanjate */}
        <Box className={classes.templatesRowDesktop}>
          {getDesktopTemplates()}
        </Box>

        {/* Mobile View - doar template-ul curent */}
        <Box className={classes.templatesRowMobile}>
          {getMobileTemplate()}
        </Box>

        <IconButton 
          className={classes.arrowButton}
          onClick={handleNext}
          disabled={isTransitioning}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      <Button 
        className={classes.downloadButton}
        onClick={handleDownload}
      >
        PRINTEAZĂ ȘABLON
      </Button>
    </Box>
  );
};

export default Emoji;